import { useState } from 'react';
import styles from './admin.module.css';

/**
 * An editable list of short strings: conversation starters, page rules. Rows
 * keep their order (they are shown to visitors in it) and can be moved; a
 * duplicate or an over-long entry is refused with a reason.
 */
export function StringListEditor({
  value,
  onChange,
  placeholder,
  addLabel = 'Add',
  emptyText,
  max,
  maxLength,
  mono = false,
  validate,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  addLabel?: string;
  emptyText: string;
  max: number;
  maxLength: number;
  mono?: boolean;
  /** A reason to refuse an entry, or null to accept it. */
  validate?: (item: string) => string | null;
}) {
  const [input, setInput] = useState('');
  const [err, setErr] = useState<string | null>(null);

  function add() {
    const item = input.trim();
    if (!item) return;
    if (item.length > maxLength) return setErr(`Keep it under ${maxLength} characters.`);
    if (value.some((v) => v.toLowerCase() === item.toLowerCase())) return setErr('Already added.');
    if (value.length >= max) return setErr(`At most ${max}.`);
    const invalid = validate?.(item);
    if (invalid) return setErr(invalid);
    onChange([...value, item]);
    setInput('');
    setErr(null);
  }

  function move(i: number, by: -1 | 1) {
    const next = [...value];
    const [item] = next.splice(i, 1);
    next.splice(i + by, 0, item!);
    onChange(next);
  }

  return (
    <div>
      <ol className={styles.listRows}>
        {value.length === 0 && <li className={styles.hint}>{emptyText}</li>}
        {value.map((item, i) => (
          <li key={item} className={styles.listRow}>
            <span className={mono ? styles.mono : undefined}>{item}</span>
            <span className={styles.listRowActions}>
              <button type="button" aria-label={`Move “${item}” up`} disabled={i === 0} onClick={() => move(i, -1)}>
                ↑
              </button>
              <button
                type="button"
                aria-label={`Move “${item}” down`}
                disabled={i === value.length - 1}
                onClick={() => move(i, 1)}
              >
                ↓
              </button>
              <button type="button" aria-label={`Remove “${item}”`} onClick={() => onChange(value.filter((_, j) => j !== i))}>
                ×
              </button>
            </span>
          </li>
        ))}
      </ol>
      <span style={{ display: 'flex', gap: 'var(--inic-space-2)' }}>
        <input
          className={styles.input}
          style={{ flex: 1, minWidth: 0 }}
          value={input}
          maxLength={maxLength + 20}
          placeholder={placeholder}
          disabled={value.length >= max}
          onChange={(e) => {
            setInput(e.target.value);
            setErr(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <button type="button" className={styles.buttonGhost} onClick={add} disabled={value.length >= max}>
          {addLabel}
        </button>
      </span>
      {err && (
        <span className={styles.error} style={{ fontSize: 'var(--inic-size-xs)' }}>
          {err}
        </span>
      )}
    </div>
  );
}
